import React, {useState, useEffect} from "react";
import {Form, Button, Card, CardBody, CardHeader, CardTitle, Row, Col, Spinner, Alert} from "reactstrap";
import {resetPassword, confirmResetPassword} from "aws-amplify/auth";
import {Link, useNavigate, useLocation} from "react-router-dom";
import AuthInput from "./AuthInput";
import AuthError from "./AuthError";
import {BackgroundColorContext} from "../../contexts/BackgroundColorContext";
import {rateLimiter} from "../../utils/rateLimiter";

type ResetStep = "request" | "confirm";

interface LocationState {
  email?: string;
}

const PasswordResetForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  const [step, setStep] = useState<ResetStep>("request");
  const [email, setEmail] = useState(() => {
    // Try to get email from location state first, then from sessionStorage
    return state?.email || sessionStorage.getItem("resetPasswordEmail") || "";
  });
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [rateLimitMessage, setRateLimitMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    email?: string;
    code?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  // Password requirements state for real-time feedback
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false
  });

  // Check rate limit status when email changes or step changes
  useEffect(() => {
    if (email) {
      const rateLimitKey =
        step === "request" ? `reset_request_${email.toLowerCase()}` : `reset_confirm_${email.toLowerCase()}`;

      const config =
        step === "request"
          ? {maxAttempts: 3, windowMs: 60 * 60 * 1000, blockDurationMs: 2 * 60 * 60 * 1000} // 3 attempts per hour, 2 hour block
          : {maxAttempts: 5, windowMs: 15 * 60 * 1000, blockDurationMs: 30 * 60 * 1000}; // 5 attempts per 15 min, 30 min block

      const status = rateLimiter.checkRateLimit(rateLimitKey, config);

      setIsRateLimited(status.isBlocked);
      if (status.isBlocked) {
        setRateLimitMessage(status.message || null);
      } else if (status.remainingAttempts <= 2) {
        setRateLimitMessage(status.message || null);
      } else {
        setRateLimitMessage(null);
      }
    }
  }, [email, step]);

  const validateRequestForm = (): boolean => {
    const errors: typeof validationErrors = {};

    if (!email) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Please enter a valid email address";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateConfirmForm = (): boolean => {
    const errors: typeof validationErrors = {};

    if (!verificationCode) {
      errors.code = "Verification code is required";
    } else if (verificationCode.length !== 6) {
      errors.code = "Verification code must be 6 digits";
    }

    // Password validation
    if (!newPassword) {
      errors.password = "Password is required";
    } else if (newPassword.length < 8) {
      errors.password = "Password must be at least 8 characters";
    } else if (!/(?=.*[a-z])/.test(newPassword)) {
      errors.password = "Password must contain at least one lowercase letter";
    } else if (!/(?=.*[A-Z])/.test(newPassword)) {
      errors.password = "Password must contain at least one uppercase letter";
    } else if (!/(?=.*\d)/.test(newPassword)) {
      errors.password = "Password must contain at least one number";
    } else if (!/(?=.*[@$!%*?&])/.test(newPassword)) {
      errors.password = "Password must contain at least one special character (@$!%*?&)";
    }

    // Confirm password validation
    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateRequestForm()) {
      return;
    }

    // Check rate limit before attempting reset request
    const rateLimitKey = `reset_request_${email.toLowerCase()}`;
    const rateLimitStatus = rateLimiter.checkRateLimit(rateLimitKey, {
      maxAttempts: 3,
      windowMs: 60 * 60 * 1000,
      blockDurationMs: 2 * 60 * 60 * 1000
    });

    if (rateLimitStatus.isBlocked) {
      setError(rateLimitStatus.message || "Too many reset attempts. Please try again later.");
      setIsRateLimited(true);
      setRateLimitMessage(rateLimitStatus.message || null);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Try with exact email first, then lowercase if user not found
      let output = null;
      let emailToUse = email;

      try {
        output = await resetPassword({
          username: email
        });
      } catch (firstError: any) {
        // If user not found and email contains uppercase, try lowercase
        if (firstError.name === "UserNotFoundException" && email !== email.toLowerCase()) {
          try {
            output = await resetPassword({
              username: email.toLowerCase()
            });
            emailToUse = email.toLowerCase();
          } catch {
            // Both attempts failed, throw the original error
            throw firstError;
          }
        } else {
          // Not a UserNotFoundException or already lowercase, throw original error
          throw firstError;
        }
      }

      if (output && output.nextStep.resetPasswordStep === "CONFIRM_RESET_PASSWORD_WITH_CODE") {
        // Record successful attempt
        rateLimiter.recordAttempt(rateLimitKey, {
          maxAttempts: 3,
          windowMs: 60 * 60 * 1000,
          blockDurationMs: 2 * 60 * 60 * 1000
        });

        // Store the email that worked for the confirmation step
        sessionStorage.setItem("resetPasswordEmail", emailToUse);
        setEmail(emailToUse); // Update state with the email that worked
        setSuccessMessage("Password reset code sent! Please check your email.");
        // Clear success message after a short delay and move to confirm step
        setTimeout(() => {
          setSuccessMessage(null);
          setStep("confirm");
        }, 2000);
      }
    } catch (err: any) {
      // Record failed attempt for rate limiting
      const attemptStatus = rateLimiter.recordAttempt(rateLimitKey, {
        maxAttempts: 3,
        windowMs: 60 * 60 * 1000,
        blockDurationMs: 2 * 60 * 60 * 1000
      });

      if (attemptStatus.isNowBlocked) {
        setIsRateLimited(true);
        setRateLimitMessage(attemptStatus.message || null);
      } else if (attemptStatus.remainingAttempts <= 2) {
        setRateLimitMessage(attemptStatus.message || null);
      }
      //console.error("Password reset error:", err);

      if (err.name === "UserNotFoundException") {
        setError("No account found with this email address.");
      } else if (err.name === "LimitExceededException") {
        setError("Too many attempts. Please try again later.");
      } else if (err.name === "InvalidParameterException") {
        setError("Invalid email address. Please check and try again.");
      } else {
        setError(err.message || "An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateConfirmForm()) {
      return;
    }

    // Check rate limit before attempting confirmation
    const rateLimitKey = `reset_confirm_${email.toLowerCase()}`;
    const rateLimitStatus = rateLimiter.checkRateLimit(rateLimitKey, {
      maxAttempts: 5,
      windowMs: 15 * 60 * 1000,
      blockDurationMs: 30 * 60 * 1000
    });

    if (rateLimitStatus.isBlocked) {
      setError(rateLimitStatus.message || "Too many attempts. Please try again later.");
      setIsRateLimited(true);
      setRateLimitMessage(rateLimitStatus.message || null);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await confirmResetPassword({
        username: email, // Use the email that worked from request step (already stored correctly)
        confirmationCode: verificationCode,
        newPassword: newPassword
      });

      // Clear stored email and rate limits
      sessionStorage.removeItem("resetPasswordEmail");
      rateLimiter.clearRateLimit(rateLimitKey);
      rateLimiter.clearRateLimit(`reset_request_${email.toLowerCase()}`);

      setSuccessMessage("Password reset successful! Redirecting to sign in...");

      // Redirect to sign in after a short delay
      setTimeout(() => {
        navigate("/auth/signin", {
          state: {
            message: "Your password has been reset. Please sign in with your new password.",
            email: email // Use the actual email that worked
          }
        });
      }, 2000);
    } catch (err: any) {
      // Record failed attempt for rate limiting
      const attemptStatus = rateLimiter.recordAttempt(rateLimitKey, {
        maxAttempts: 5,
        windowMs: 15 * 60 * 1000,
        blockDurationMs: 30 * 60 * 1000
      });

      if (attemptStatus.isNowBlocked) {
        setIsRateLimited(true);
        setRateLimitMessage(attemptStatus.message || null);
      } else if (attemptStatus.remainingAttempts < 3) {
        setRateLimitMessage(attemptStatus.message || null);
      }
      //console.error("Password reset confirmation error:", err);

      if (err.name === "CodeMismatchException") {
        setError("Invalid verification code. Please check and try again.");
      } else if (err.name === "ExpiredCodeException") {
        setError("Verification code has expired. Please request a new code.");
      } else if (err.name === "InvalidPasswordException") {
        setError("Password does not meet requirements. Please check the password requirements below.");
      } else {
        setError(err.message || "An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (step === "request") {
        handleRequestReset(e as any);
      } else {
        handleConfirmReset(e as any);
      }
    }
  };

  return (
    <BackgroundColorContext.Consumer>
      {({color}) => (
        <Card className="auth-card">
          <CardHeader data-color={color}>
            <CardTitle tag="h3">{step === "request" ? "Reset Password" : "Set New Password"}</CardTitle>
          </CardHeader>
          <CardBody>
            {step === "request" ? (
              <Form onSubmit={handleRequestReset}>
                <Alert color="info" className="mb-4">
                  <span className="alert-inner--text">
                    Enter your email address and we'll send you a code to reset your password.
                  </span>
                </Alert>

                <AuthError error={error} onClose={() => setError(null)} />

                {rateLimitMessage && !error && (
                  <Alert color={isRateLimited ? "danger" : "warning"}>
                    <span className="alert-inner--icon">
                      <i className={`tim-icons icon-${isRateLimited ? "alert-circle-exc" : "bulb-63"}`} />
                    </span>
                    <span className="alert-inner--text ml-1">{rateLimitMessage}</span>
                  </Alert>
                )}

                {successMessage && (
                  <Alert color="success">
                    <span className="alert-inner--icon">
                      <i className="tim-icons icon-check-2" />
                    </span>
                    <span className="alert-inner--text ml-1">{successMessage}</span>
                  </Alert>
                )}

                <AuthInput
                  id="email"
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (validationErrors.email) {
                      setValidationErrors({...validationErrors, email: undefined});
                    }
                  }}
                  placeholder="Enter your email"
                  error={validationErrors.email}
                  required
                  autoComplete="email"
                  disabled={loading}
                  onKeyPress={handleKeyPress}
                />

                <Row className="mt-4">
                  <Col>
                    <Button
                      color="primary"
                      type="submit"
                      block
                      disabled={loading || !!successMessage || isRateLimited}
                      data-color={color}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="mr-2" />
                          <span>Sending Code...</span>
                        </>
                      ) : (
                        <span>Send Reset Code</span>
                      )}
                    </Button>
                  </Col>
                </Row>

                <div className="text-center mt-3">
                  <Link to="/auth/signin" className="text-muted">
                    Back to Sign In
                  </Link>
                </div>
              </Form>
            ) : (
              <Form onSubmit={handleConfirmReset}>
                <Alert color="info" className="mb-4">
                  <span className="alert-inner--text">
                    Enter the verification code sent to <strong>{email}</strong> and your new password.
                  </span>
                </Alert>

                <AuthError error={error} onClose={() => setError(null)} />

                {rateLimitMessage && !error && (
                  <Alert color={isRateLimited ? "danger" : "warning"}>
                    <span className="alert-inner--icon">
                      <i className={`tim-icons icon-${isRateLimited ? "alert-circle-exc" : "bulb-63"}`} />
                    </span>
                    <span className="alert-inner--text ml-1">{rateLimitMessage}</span>
                  </Alert>
                )}

                {successMessage && (
                  <Alert color="success">
                    <span className="alert-inner--icon">
                      <i className="tim-icons icon-check-2" />
                    </span>
                    <span className="alert-inner--text ml-1">{successMessage}</span>
                  </Alert>
                )}

                <AuthInput
                  id="verificationCode"
                  label="Verification Code"
                  type="text"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 6) {
                      setVerificationCode(value);
                      if (validationErrors.code) {
                        setValidationErrors({...validationErrors, code: undefined});
                      }
                    }
                  }}
                  placeholder="Enter 6-digit code"
                  error={validationErrors.code}
                  required
                  maxLength={6}
                  pattern="[0-9]{6}"
                  disabled={loading}
                />

                <AuthInput
                  id="newPassword"
                  label="New Password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewPassword(value);

                    // Update password requirements in real-time
                    setPasswordRequirements({
                      minLength: value.length >= 8,
                      hasUppercase: /[A-Z]/.test(value),
                      hasLowercase: /[a-z]/.test(value),
                      hasNumber: /\d/.test(value),
                      hasSpecialChar: /[@$!%*?&]/.test(value)
                    });

                    if (validationErrors.password) {
                      setValidationErrors({...validationErrors, password: undefined});
                    }
                  }}
                  placeholder="Enter new password"
                  error={validationErrors.password}
                  required
                  autoComplete="new-password"
                  disabled={loading}
                />

                <div className="password-requirements mb-3">
                  <p className="text-muted small mb-2">Password must contain:</p>
                  <div className="requirement-list">
                    <div className={`requirement-item ${passwordRequirements.minLength ? "met" : ""}`}>
                      <span className="requirement-indicator">
                        {passwordRequirements.minLength ? (
                          <i className="tim-icons icon-check-2" />
                        ) : (
                          <i className="tim-icons icon-simple-remove" />
                        )}
                      </span>
                      <span className="requirement-text">At least 8 characters</span>
                    </div>
                    <div className={`requirement-item ${passwordRequirements.hasUppercase ? "met" : ""}`}>
                      <span className="requirement-indicator">
                        {passwordRequirements.hasUppercase ? (
                          <i className="tim-icons icon-check-2" />
                        ) : (
                          <i className="tim-icons icon-simple-remove" />
                        )}
                      </span>
                      <span className="requirement-text">One uppercase letter</span>
                    </div>
                    <div className={`requirement-item ${passwordRequirements.hasLowercase ? "met" : ""}`}>
                      <span className="requirement-indicator">
                        {passwordRequirements.hasLowercase ? (
                          <i className="tim-icons icon-check-2" />
                        ) : (
                          <i className="tim-icons icon-simple-remove" />
                        )}
                      </span>
                      <span className="requirement-text">One lowercase letter</span>
                    </div>
                    <div className={`requirement-item ${passwordRequirements.hasNumber ? "met" : ""}`}>
                      <span className="requirement-indicator">
                        {passwordRequirements.hasNumber ? (
                          <i className="tim-icons icon-check-2" />
                        ) : (
                          <i className="tim-icons icon-simple-remove" />
                        )}
                      </span>
                      <span className="requirement-text">One number</span>
                    </div>
                    <div className={`requirement-item ${passwordRequirements.hasSpecialChar ? "met" : ""}`}>
                      <span className="requirement-indicator">
                        {passwordRequirements.hasSpecialChar ? (
                          <i className="tim-icons icon-check-2" />
                        ) : (
                          <i className="tim-icons icon-simple-remove" />
                        )}
                      </span>
                      <span className="requirement-text">One special character (@$!%*?&)</span>
                    </div>
                  </div>
                </div>

                <AuthInput
                  id="confirmPassword"
                  label="Confirm New Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (validationErrors.confirmPassword) {
                      setValidationErrors({...validationErrors, confirmPassword: undefined});
                    }
                  }}
                  placeholder="Confirm new password"
                  error={validationErrors.confirmPassword}
                  required
                  autoComplete="new-password"
                  disabled={loading}
                  onKeyPress={handleKeyPress}
                />

                <Row className="mt-4">
                  <Col>
                    <Button
                      color="primary"
                      type="submit"
                      block
                      disabled={loading || !!successMessage || isRateLimited}
                      data-color={color}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="mr-2" />
                          <span>Resetting Password...</span>
                        </>
                      ) : (
                        <span>Reset Password</span>
                      )}
                    </Button>
                  </Col>
                </Row>

                <div className="text-center mt-3">
                  <Button
                    color="link"
                    className="text-muted"
                    onClick={() => {
                      setStep("request");
                      setVerificationCode("");
                      setNewPassword("");
                      setConfirmPassword("");
                      setError(null);
                      setSuccessMessage(null);
                    }}
                  >
                    Request New Code
                  </Button>
                </div>
              </Form>
            )}
          </CardBody>
        </Card>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default PasswordResetForm;
